import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PiUploadSimple,
  PiClipboardText,
  PiMapPin,
  PiRoadHorizon,
} from "react-icons/pi";
import { FaVideo } from "react-icons/fa";
import { HiDocumentReport } from "react-icons/hi";
import { MdLogout, MdDashboard, MdSettings, MdOutlineConfirmationNumber, MdCompareArrows } from "react-icons/md";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useBranding } from "../../contexts/BrandingContext";
import useAuth from "../../hooks/useAuth";
import RVlogo from "../../assets/rv_logo.png";

const adminModules = [
  { key: "dashboard", label: "Dashboard", icon: MdDashboard, route: "/home" },
  { key: "gis", label: "GIS", icon: PiMapPin, route: null },
  { key: "report", label: "Report", icon: HiDocumentReport, route: "/pavement/app" },
  { key: "prepost", label: "Pre/Post Analysis", icon: MdCompareArrows, route: "/pavement/prePostAnalysis" },
  // { key: "road", label: "Road", icon: PiUploadSimple, route: "/road/dashcam" },
  { key: "roadSurveys", label: "Road Surveys", icon: PiRoadHorizon, route: "/road/surveys" },
  // { key: "survey", label: "Surveys", icon: PiClipboardText, route: "/pavement/viewTask" },
  { key: "video", label: "Video Library", icon: FaVideo, route: "/video/app" },
  { key: "tickets", label: "Tickets", icon: MdOutlineConfirmationNumber, route: "/pavement/tickets" },
  { key: "settings", label: "Settings", icon: MdSettings, route: "/pavement/settings" },
];

const surveyorModules = [
  // { key: "survey", label: "Survey", icon: PiClipboardText, route: "/pavement/viewTask" },
  // { key: "road", label: "Road", icon: PiUploadSimple, route: "/road/dashcam" },
  { key: "roadSurveys", label: "Road Surveys", icon: PiRoadHorizon, route: "/road/surveys" },
  { key: "videoLibrary", label: "Video Library", icon: FaVideo, route: "/video/app" },
  { key: "tickets", label: "Tickets", icon: MdOutlineConfirmationNumber, route: "/pavement/tickets" },
];

const AppSidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { brandingSettings } = useBranding();
  const { role, user, logout } = useAuth();
  const isSurveyor = role === "Surveyor";
  const isBhilwaraUser = (user?.email || "").toLowerCase() === "bhilwarauser@roadvision.ai";
  const modules = isSurveyor
    ? surveyorModules
    : isBhilwaraUser
      ? adminModules
      : adminModules.filter(m => m.key !== "prepost");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getActiveKey = () => {
    const path = location.pathname;
    if (path === "/home") return "dashboard";
    if (path.includes("/multiMap")) return "gis";
    if (path.includes("/viewTask") || path.includes("/myTask")) return "survey";
    if (path.includes("/video")) return "video";
    if (path.includes("/prePostAnalysis")) return "prepost";
    if (path.includes("/pavement/app") || path.includes("/report") || path.includes("/multiReport")) return "report";
    if (path.includes("/settings") || path.includes("/manageUser") || path.includes("/profileBranding")) return "settings";
    if (path.includes("/pavement/tickets")) return "tickets";
    if (path.includes("/road/surveys")) return "roadSurveys";
    if (path.includes("/road") || path.includes("/dashcam")) return "road";
    return "dashboard";
  };

  const activeKey = getActiveKey();

  const handleModuleClick = (mod) => {
    if (mod.key === "gis") {
      // Navigate instantly — MultiMap will fetch its own data
      navigate("/pavement/multiMap");
      return;
    }
    if (mod.route) {
      navigate(mod.route);
    }
  };

  return (
    <aside
      className={`h-screen flex flex-col border-r border-[#1a3a5c] transition-all duration-300 ease-in-out flex-shrink-0 ${
        collapsed ? "w-[68px]" : "w-[230px]"
      }`}
      style={{ position: "relative", backgroundColor: "hsl(217, 64%, 18%)", fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Logo */}
      <div className={`flex items-center justify-center border-b border-white/10 ${collapsed ? "px-2 py-3" : "px-5 py-4"}`}>
        <img
          src={collapsed ? "/rvnewlog-removebg.png" : RVlogo}
          alt="Logo"
          className={`object-contain ${collapsed ? "w-10 h-10" : "h-11 w-auto max-w-[195px]"}`}
        />
      </div>

      {/* Category Label */}
      {!collapsed && !isSurveyor && (
        <div className="px-5 pt-3 pb-1">
          <span className="text-[11px] font-semibold tracking-widest text-white/50 uppercase">
            Road Intelligence
          </span>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-3">
        {modules.map((mod, idx) => {
          const isActive = activeKey === mod.key;
          const Icon = mod.icon;

          // Add category separator before management items
          const showManagementLabel = !collapsed && !isSurveyor && mod.key === "roadSurveys";

          return (
            <React.Fragment key={mod.key}>
              {showManagementLabel && (
                <div className="px-2 pt-3 pb-1">
                  <span className="text-[11px] font-semibold tracking-widest text-white/50 uppercase">
                    Management
                  </span>
                </div>
              )}
              <button
                onClick={() => handleModuleClick(mod)}
                className={`w-full flex items-center gap-3 transition-all duration-200 rounded-lg mb-0.5 ${
                  collapsed ? "px-0 py-2.5 justify-center" : "px-4 py-2.5"
                } ${
                  isActive
                    ? "bg-white/15 text-white font-semibold"
                    : "text-white/80 hover:bg-white/10 hover:text-white"
                }`}
                title={collapsed ? mod.label : ""}
              >
                <div
                  className={`flex items-center justify-center flex-shrink-0 ${
                    collapsed ? "w-5 h-5" : "w-5 h-5"
                  }`}
                >
                  <Icon size={collapsed ? 19 : 17} />
                </div>
                {!collapsed && (
                  <span className="text-[14px] font-medium whitespace-nowrap">
                    {mod.label}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="flex flex-col">
        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`w-full py-2 flex items-center gap-3 border-t border-white/10 text-white/70 hover:text-white transition-all ${
            collapsed ? "justify-center px-3" : "px-4"
          }`}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <IoChevronForward size={16} /> : <IoChevronBack size={16} />}
          {!collapsed && <span className="text-[13px]">Collapse</span>}
        </button>

        {/* User Info */}
        <div className={`flex items-center gap-2.5 py-2 border-t border-white/10 ${collapsed ? "justify-center px-3" : "px-4"}`}>
          <div className="w-8 h-8 rounded-full bg-[#1a2d54] flex items-center justify-center flex-shrink-0 border-2 border-blue-400/50">
            <span className="text-[11px] font-bold text-white">
              {(user?.name || "U")[0].toUpperCase()}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] font-semibold text-white truncate">
                  {user?.name || "User"}
                </span>
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-500/30 text-blue-200 tracking-wider flex-shrink-0">
                  {role || "User"}
                </span>
              </div>
              <div className="text-[10px] text-white/50 truncate">
                {user?.email || ""}
              </div>
            </div>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`w-full py-2 flex items-center gap-3 text-white/70 hover:text-red-400 transition-all ${
            collapsed ? "justify-center px-3" : "px-4"
          }`}
          title="Logout"
        >
          <MdLogout size={16} />
          {!collapsed && <span className="text-[13px]">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default AppSidebar;
